import { useState, useEffect } from "react";
import { useAppSelector } from "src/app/hooks";
import { selectAuth } from "src/features/authSlice";
import { HttpMethod, callApi } from "src/services/apiService";

interface DocumentItem {
  title: string;
  filename: string;
  description?: string;
  logo?: string;
}

interface DocumentCategory {
  title: string;
  description: string;
  folder: string;
  documents: DocumentItem[];
}

const Docs = () => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(false);
  const [showSalesReport, setShowSalesReport] = useState<boolean>(false);
  const { isSysadmin, acctId } = useAppSelector(selectAuth);

  // Detect theme changes
  useEffect(() => {
    const checkTheme = () => {
      const bodyTheme = document.body.getAttribute("base-theme");
      setIsDarkTheme(bodyTheme === "dark");
    };

    // Check initial theme
    checkTheme();

    // Set up observer for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "base-theme"
        ) {
          checkTheme();
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["base-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Fetch access permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const apiUrl = `Header`;
        const response = await callApi(apiUrl, HttpMethod.GET);
        if (response) {
          setShowSalesReport(response.showSalesReport);
        }
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
      }
    };

    fetchPermissions();
  }, [acctId]);

  // Get the appropriate Zapier logo based on theme
  const getZapierLogo = () => {
    return isDarkTheme
      ? "/images/integrations/zapier-logo_white.png"
      : "/images/integrations/zapier-logo_black.png";
  };

  // Configuration object for easy maintenance - similar to markdown structure
  const documentationConfig: DocumentCategory[] = [
    {
      title: "Do Not Call Scrub Documentation",
      description: "Documentation for Do Not Call services",
      folder: "dncscrub",
      documents: [
        {
          title: "CCC Output Guide",
          filename: "CCC Output Guide 20240911 v3.pdf",
          description:
            "Comprehensive guide to CCC output formats and data structures",
        },
        {
          title: "CCC Scrub API Documentation",
          filename: "CCC Scrub API Documentation 20230417.pdf",
          description:
            "API documentation for CCC Full Scrub API. Includes Federal, State, Internal DNC, EBR, time, holiday Do Not Call scrubbing, and adding records to Internal DNC and EBR",
        },
        {
          title: "CCC Litigator Only API",
          filename: "CCC Litigator Only API v1.61.pdf",
          description:
            "API Documentation for the Litigator-Only API. Only to be used if Federal, State, Internal DNC, EBR, time, and holiday scrubbing are not needed",
        },
        {
          title: "C# API Scrub Example",
          filename: "CSharpaAPIScrubExample.cs",
          description: "Sample C# code implementation for API integration",
        },
        {
          title: "JavaScript API Sample",
          filename: "javascript_scrub_sample_html",
          description:
            "Sample JavaScript/HTML code implementation for API integration",
        },
      ],
    },
    {
      title: "Reassigned Scrub Documentation",
      description: "Documentation for Reassigned Scrub services",
      folder: "reassigned-scrub",
      documents: [
        {
          title: "RND Basic API",
          filename: "RND Basic API.pdf",
          description: "Basic API documentation for RND services",
        },
        {
          title: "RND Basic File Format",
          filename: "RND Basic File Format v1.0.pdf",
          description: "File format specifications for RND data",
        },
        {
          title: "TCPA Authority API",
          filename: "TCPA Authority API v2.02.pdf",
          description: "TCPA Authority API reference documentation",
        },
        {
          title: "TCPA Authority Plus API",
          filename: "TCPA Authority Plus API v2.0.pdf",
          description: "Enhanced TCPA Authority API documentation",
        },
        {
          title: "TCPA Authority Plus File Format",
          filename: "TCPA Authority Plus File Format v2.1.pdf",
          description: "File format specifications for TCPA Authority Plus",
        },
        {
          title: "TCPA Authority Plus Test Numbers",
          filename: "TCPA Authority Plus Test Numbers.pdf",
          description: "Test numbers and sample data for TCPA Authority Plus",
        },
        {
          title: "TCPA Reassigned Authority File Format",
          filename: "TCPA Reassigned Authority File Format v2.4.pdf",
          description:
            "File format specifications for TCPA Reassigned Authority",
        },
      ],
    },
    {
      title: "TrustCall Documentation",
      description: "Documentation for TrustCall services",
      folder: "trustcall",
      documents: [
        {
          title: "TrustCall Premier API",
          filename: "TrustCall Premier API v20240204.pdf",
          description:
            "API documentation for TrustCall Premier Monitor services",
        },
        {
          title: "TrustCall One Time Scan API",
          filename: "TrustCall One Time Scan API v20240402.pdf",
          description:
            "API documentation for TrustCall One Time Scan services available to carriers only",
        },
      ],
    },
    {
      title: "Integrations",
      description: "Platform integrations and implementation guides",
      folder: "integrations",
      documents: [
        {
          title: "Genesys PureCloud",
          filename:
            "https://help.mypurecloud.com/articles/use-dnc-scrubbing-provided-contact-center-compliance-corporation/",
          description:
            "Configure CCC DNC scrubbing integration with Genesys Cloud. Genesys supports DNC.com (Contact Center Compliance Corporation) as a web service provider for just-in-time DNC scrubbing to ensure compliance with legislative requirements. Requires a Login ID from Contact Center Compliance to authorize service access.",
          logo: "/images/integrations/genesys-logo.png",
        },
        {
          title: "Zoom Phone",
          filename:
            "https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0077692#:~:text=Contact%20Center%20Compliance-,Enabling%20Do%20Not%20Call%20integrations%20with%20Gryphon%20and%20Contact%20Center,use%20the%20DNC.com%20integration",
          description:
            "Enable Do Not Call integrations with Contact Center Compliance Corporation (DNC.com) in Zoom Phone. This integration allows Zoom Phone users to leverage CCC's DNC scrubbing services to ensure compliance with federal and state Do Not Call regulations during outbound calling campaigns.",
          logo: "/images/integrations/zoom-logo.png",
        },
        {
          title: "Zapier Automation Platform",
          filename:
            "https://zapier.com/developer/public-invite/201522/e92e8a08f7a9fe7fca3ceea20c65d318/",
          description:
            "Automate Do Not Call scrubbing workflows with CCC's Zapier integration. This app enables automated TCPA compliance by scrubbing contact lists against known serial plaintiffs and litigators as part of your workflow automation. Perfect for call centers and marketing teams who need to integrate litigator scrubbing into their existing automation processes across 8,000+ connected apps.",
          logo: getZapierLogo(),
        },
        {
          title: "DialedIn by ChaseData",
          filename:
            "https://docs.chasedatacorp.com/docs-page-integration-portal.html#item-3-28",
          description:
            "Configure DNC Compliance workflows in ChaseData's Integration Portal. The platform supports DNC scrubbing integration through workflow steps that can be combined with field mapping, CRM integrations, and automated compliance processes. Workflows are executed via API requests to the ChaseData integration endpoint with authentication tokens.",
          logo: "/images/integrations/chasedata-logo.png",
        },
        {
          title: "Legacy Salesforce - Visualforce Pages",
          filename: "CCC SFDC Package Install Guide v20231116.pdf",
          description:
            "Salesforce integration guide for Visualforce pages only. For advanced integrations, use our JSON REST API instead",
          logo: "/images/integrations/salesforce-logo.png",
        },
        {
          title: "CallShaper - DNCScrub Integration",
          filename: "CallShaper - DNCScrub Integration v0082025.pdf",
          description:
            "Integration guide for CallShaper dialer with DNC Scrubbing services. Covers configuration and setup for automated compliance workflows.",
        },
      ],
    },
    {
      title: "Additional Resources",
      description: "API collections, rate limits, and other helpful resources",
      folder: "Other",
      documents: [
        {
          title: "Postman API Collection",
          filename:
            "Scrub APIs Samples for Clients v20240401.postman_collection.json",
          description:
            "Postman collection with pre-configured API requests and examples",
        },
        {
          title: "CCC Data API",
          filename: "CCC Data API v1.10.pdf",
          description: "Documentation on CCC Data API",
        },
        {
          title: "CCC Global Connect API",
          filename: "CCC Global Connect API.pdf",
          description: "Documentation for CCC Global Connect API services",
        },
        {
          title: "API Rates, File Size Limits, and Average Response Times",
          filename: "CCC API Rate and File Size Limits v20250702.pdf",
          description:
            "Important information about API rate limits, file size restrictions, and average response times",
        },
        {
          title: "CCC Compliance Guide API",
          filename: "CCC Compliance Guide API v1.pdf",
          description:
            "Compliance Guide API that allows for retrieving Holiday and State of Emergency information. Must have Compliance Guide feature to use",
        },
        {
          title: "CCC OAuth Authorization",
          filename: "CCC OAuth Authorization v20251125.pdf",
          description:
            "OAuth 2.0 authorization documentation for secure API authentication and access token management",
        },
      ],
    },
  ];

  // Add restricted sections based on user permissions
  const getFilteredDocumentationConfig = () => {
    const config = [...documentationConfig];

    // Add Push Files section for users with Sales Report access
    if (isSysadmin || showSalesReport) {
      config.push({
        title: "Push Files - Not Public",
        description:
          "Internal push files and restricted documentation (limited access)",
        folder: "internal-docs",
        documents: [
          {
            title: "CCC Litigator Push Documentation",
            filename: "CCC Litigator Push Documentation v20240122.pdf",
            description:
              "Comprehensive documentation for CCC Litigator Push services and implementation",
          },
          {
            title:
              "MasterList, Wireless Portability, and WirelessVoIP Prefix Push Documentation",
            filename:
              "Custom Data Solution for Internal Compliance Systems 4.6.pdf",
            description:
              "Internal compliance systems push solution guide and specifications",
          },
          {
            title: "Master List Sample Data",
            filename: "MasterList Sample.csv",
            description:
              "Sample master list data file for testing and reference",
          },
          {
            title: "Litigator Sample Data",
            filename: "Litigator Sample.csv",
            description: "Sample litigator data file for testing and reference",
          },
        ],
      });
    }

    return config;
  };

  const toggleCategory = (categoryTitle: string) => {
    setExpandedCategory(
      expandedCategory === categoryTitle ? null : categoryTitle,
    );
  };

  const handleDocumentClick = (folder: string, filename: string) => {
    if (filename.startsWith("http://") || filename.startsWith("https://")) {
      window.open(filename, "_blank");
    } else {
      const url = `/docs/${folder}/${filename}`;
      window.open(url, "_blank");
    }
  };

  const getFileIcon = (filename: string) => {
    const extension = filename.split(".").pop()?.toLowerCase();
    // Handle files without extensions (like javascript_scrub_sample_html)
    if (filename.includes("javascript") || filename.includes("html")) {
      return "bi-file-earmark-code";
    }
    switch (extension) {
      case "pdf":
        return "bi-file-earmark-pdf";
      case "cs":
        return "bi-file-earmark-code";
      case "js":
        return "bi-file-earmark-code";
      case "json":
        return "bi-file-earmark-code";
      case "html":
        return "bi-file-earmark-code";
      default:
        return "bi-file-earmark";
    }
  };

  return (
    <div className="container-fluid mb-5">
      <div className="row">
        <div className="col-12">
          <h1>Documentation</h1>
          <p className="text-muted mb-4">
            Access our comprehensive documentation, API guides, and technical
            resources. All documents open in a new tab for easy reference.
          </p>

          <div className="alert alert-success d-flex align-items-center mb-4">
            <i className="bi bi-stars me-2 fs-5"></i>
            <div>
              <strong>Try our new documentation site!</strong>{" "}
              <a
                href="https://docs.dncscrub.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="alert-link"
              >
                docs.dncscrub.com
              </a>
              <span className="ms-2 text-muted">
                — Below is our legacy documentation.
              </span>
            </div>
          </div>

          {getFilteredDocumentationConfig().map((category) => (
            <div key={category.title} className="card mb-3">
              <div className="card-header">
                <button
                  className="btn btn-link text-decoration-none p-0 w-100 text-start d-flex justify-content-between align-items-center"
                  onClick={() => toggleCategory(category.title)}
                  aria-expanded={expandedCategory === category.title}
                >
                  <div>
                    <h5 className="mb-1">{category.title}</h5>
                    <small className="text-muted">{category.description}</small>
                  </div>
                  <i
                    className={`bi ${expandedCategory === category.title ? "bi-chevron-up" : "bi-chevron-down"}`}
                  ></i>
                </button>
              </div>

              {expandedCategory === category.title && (
                <div className="card-body">
                  <div className="row">
                    {category.documents.map((doc) => (
                      <div
                        key={doc.filename}
                        className="col-12 col-md-6 col-lg-4 mb-3"
                      >
                        <div className="card h-100 border-0 shadow-sm">
                          <div className="card-body d-flex flex-column">
                            <div className="d-flex align-items-start mb-2">
                              {doc.logo ? (
                                <img
                                  src={doc.logo}
                                  alt={`${doc.title} logo`}
                                  className="me-2 mt-1"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    objectFit: "contain",
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const fallbackIcon = e.currentTarget
                                      .nextElementSibling as HTMLElement;
                                    if (fallbackIcon)
                                      fallbackIcon.style.display =
                                        "inline-block";
                                  }}
                                />
                              ) : null}
                              <i
                                className={`${getFileIcon(doc.filename)} text-primary me-2 mt-1 ${doc.logo ? "d-none" : ""}`}
                              ></i>
                              <div className="flex-grow-1">
                                {doc.filename.startsWith("http://") ||
                                doc.filename.startsWith("https://") ? (
                                  <h6 className="card-title mb-1">
                                    <a
                                      href={doc.filename}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-decoration-none"
                                    >
                                      {doc.title}
                                    </a>
                                  </h6>
                                ) : (
                                  <h6 className="card-title mb-1">
                                    {doc.title}
                                  </h6>
                                )}
                                {!(
                                  doc.filename.startsWith("http://") ||
                                  doc.filename.startsWith("https://")
                                ) && (
                                  <small className="text-muted">
                                    {doc.filename}
                                  </small>
                                )}
                              </div>
                            </div>
                            {doc.description && (
                              <p className="card-text small text-muted flex-grow-1">
                                {doc.description}
                              </p>
                            )}
                            <button
                              className="btn btn-outline-primary btn-sm mt-auto"
                              onClick={() =>
                                handleDocumentClick(
                                  category.folder,
                                  doc.filename,
                                )
                              }
                            >
                              <i className="bi bi-download me-1"></i>
                              Open
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="alert alert-info mt-4">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Need Help?</strong> If you can't find the documentation
            you're looking for, please{" "}
            <a href="/help/new" className="alert-link">
              create a support ticket
            </a>{" "}
            and we'll be happy to assist you.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Docs;
